import { type ReactNode, useMemo, useRef } from 'react';

import { useIsomorphicLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect';

type SlidePosition = 'left' | 'center' | 'right';

/**
 * The last navigation direction: `-1` for backward, `1` for forward, and `0`
 * when there is no directional hint, such as a direct jump to a slide.
 *
 * Load-bearing for 2 slide carousels only — do not drop it as unused. There a
 * forward and a backward move land on the same index, so the hint is the only
 * thing that distinguishes them. From 3 slides up the shortest-wrap fallback in
 * `getVirtualStep` already resolves single-step navigation on its own.
 */
type NavigationDirection = -1 | 0 | 1;

interface UseSlidesArguments {
  activeIndex: number;
  navigationDirection?: NavigationDirection;
  render: (args: {
    slideIndex: number;
    slidePosition: SlidePosition;
    virtualIndex: number;
  }) => ReactNode;
  shouldLoop: boolean;
  totalSlides: number;
}

interface TrackedState {
  activeIndex: number;
  shouldLoop: boolean;
  totalSlides: number;
  virtualIndex: number;
}

const slots: Array<{ offset: number; slidePosition: SlidePosition }> = [
  { offset: -1, slidePosition: 'left' },
  { offset: 0, slidePosition: 'center' },
  { offset: 1, slidePosition: 'right' },
];

const getVirtualStep = (args: {
  from: number;
  navigationDirection: NavigationDirection;
  shouldLoop: boolean;
  to: number;
  totalSlides: number;
}) => {
  const { from, navigationDirection, shouldLoop, to, totalSlides } = args;
  const direct = to - from;

  if (!shouldLoop || totalSlides < 2) {
    return direct;
  }

  const wrapped = direct > 0 ? direct - totalSlides : direct + totalSlides;
  const forward = direct > 0 ? direct : wrapped;
  const backward = direct > 0 ? wrapped : direct;

  if (navigationDirection > 0) {
    return forward;
  }

  if (navigationDirection < 0) {
    return backward;
  }

  /*
   * Fall back to the shortest wrap. With 2 slides both wraps are a single step,
   * so this tie-break cannot tell them apart and always reads as forward —
   * `navigationDirection` above is what keeps those carousels animating the way
   * the user actually moved. See CDS-6090.
   */
  return Math.abs(forward) <= Math.abs(backward) ? forward : backward;
};

/**
 * A hook that takes information about the active slide in a photo carousel
 * and returns an array that contains the active slide, the previous slide,
 * and the next slide.
 *
 * The rendered slide keys use a virtual index so the three DOM nodes shift by
 * one per navigation instead of rotating or duplicating.
 *
 * @example
 * const content = useSlidesRange({
 *   activeIndex,
 *   render,
 *   shouldLoop,
 *   totalSlides,
 * });
 */
export const useSlidesRange = (args: UseSlidesArguments) => {
  const { activeIndex, navigationDirection = 0, render, shouldLoop, totalSlides } = args;

  /*
   * `trackedRef` is the last *committed* snapshot. Render reads it to derive the
   * next virtual index; the layout effect below writes it after commit, so a
   * pass React throws away (Suspense, an interrupted transition) cannot poison
   * the next step, and StrictMode's double invoke is a pure re-read. This
   * replaces `setState` during render, which made React discard the pass and
   * render `PhotoCarousel.Slides` twice per slide change.
   */
  /* oxlint-disable react/refs -- Previous-commit bookkeeping read during render to derive render output; see above. */
  const trackedRef = useRef<TrackedState>({
    activeIndex,
    shouldLoop,
    totalSlides,
    virtualIndex: activeIndex,
  });
  const tracked = trackedRef.current;

  let virtualIndex = tracked.virtualIndex;

  if (tracked.shouldLoop !== shouldLoop || tracked.totalSlides !== totalSlides) {
    virtualIndex = activeIndex;
  } else if (tracked.activeIndex !== activeIndex) {
    virtualIndex =
      tracked.virtualIndex +
      getVirtualStep({
        from: tracked.activeIndex,
        navigationDirection,
        shouldLoop,
        to: activeIndex,
        totalSlides,
      });
  }

  useIsomorphicLayoutEffect(() => {
    trackedRef.current = { activeIndex, shouldLoop, totalSlides, virtualIndex };
  });

  /*
   * Memoize on the destructured primitives + render callback rather than
   * on the `args` object — callers invoke this with an inline object
   * literal, so depending on `[args]` would invalidate the memo on every
   * render and produce a fresh `Array<ReactNode>` reference.
   */
  return useMemo(() => {
    if (totalSlides < 1) {
      return [];
    }

    return slots.map(({ offset, slidePosition }) => {
      const slotVirtualIndex = virtualIndex + offset;

      // Without looping there is nothing to render beyond either end.
      if (!shouldLoop && (slotVirtualIndex < 0 || slotVirtualIndex > totalSlides - 1)) {
        return undefined;
      }

      // `%` keeps the sign of the dividend, so shift back into range for
      // virtual indexes that have counted past the first slide.
      const slideIndex = ((slotVirtualIndex % totalSlides) + totalSlides) % totalSlides;

      return render({ slideIndex, slidePosition, virtualIndex: slotVirtualIndex });
    });
  }, [render, shouldLoop, totalSlides, virtualIndex]);
  /* oxlint-enable react/refs */
};
